import { DatePipe } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { ProjectKeyValEntry } from '@shared/ProjectKeyValStore';
import { TableModule } from 'primeng/table';
import { Subscription } from 'rxjs';
import { ProjectKeyvalAdminService } from 'src/app/shared/services/project-keyval-admin.service';
import { ProjectService } from 'src/app/shared/services/project.service';
import { SocketAdminService } from '@shared-angular/services/socketadmin.service';
import { ProjectSubmenuComponent } from 'src/app/project-submenu/project-submenu.component';
import { fromGroupchatType, isSystemKeyValType } from '@shared/ProjectKeyValStore';

export interface BotChatChannel {
  channel: string;
  bots: string[];
  messageCount: number;
  lastActivity: string;
}

@Component({
  imports: [DatePipe,  TableModule, ProjectSubmenuComponent],
  templateUrl: './project-groupchat-tab.component.html'
})
export class ProjectGroupchatTabComponent implements OnInit, OnDestroy {
  public chatChannels: BotChatChannel[] = [];
  private keyvals: ProjectKeyValEntry<string>[] = [];
  private subs: Subscription[] = [];

  constructor(
    private adminService: ProjectKeyvalAdminService,
    private projectService: ProjectService,
    private socketService: SocketAdminService,
    private router: Router
  ) { }

  private get projectId(): string {
    return this.projectService.selectedProject?._id ?? '-';
  }

  public async ngOnInit() {
    await this.loadData();

    this.subs.push(
      this.socketService.onProjectKeyValChange.subscribe((entry) => {
        if (entry.projectId != this.projectService.selectedProject?._id) return;
        const strEntry = { ...entry, value: String(entry.value) } as ProjectKeyValEntry<string>;
        const index = this.keyvals.findIndex(kv => kv._id === entry._id);
        if (index > -1) {
          this.keyvals[index] = strEntry;
        } else {
          this.keyvals.push(strEntry);
        }
        this.buildChatChannels();
      }),
      this.socketService.onProjectKeyValClean.subscribe(() => {
        void this.loadData();
      }),
      this.socketService.onProjectKeyValDelete.subscribe(() => {
        this.keyvals = [];
        this.buildChatChannels();
      })
    );
  }

  public ngOnDestroy() {
    this.subs.forEach(s => s.unsubscribe());
  }

  private async loadData() {
    const keyvals = await this.adminService.getProjectKeyVals(this.projectId);
    this.keyvals = keyvals.map(kv => ({ ...kv, value: String(kv.value) }));
    this.buildChatChannels();
  }

  private buildChatChannels() {
    const msgKeyvals = this.keyvals.filter(kv => kv.key.startsWith('msg') && isSystemKeyValType(kv.type));
    const channelMap = new Map<string, { bots: Set<string>; count: number; lastKey: string }>();

    for (const kv of msgKeyvals) {
      const channel = fromGroupchatType(kv.type);
      if (!channelMap.has(channel)) {
        channelMap.set(channel, { bots: new Set(), count: 0, lastKey: '' });
      }
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      const entry = channelMap.get(channel)!;
      entry.count++;
      if (kv.key > entry.lastKey) {
        entry.lastKey = kv.key;
      }
      const botName = this.extractBotName(kv.value);
      if (botName) {
        entry.bots.add(botName);
      }
    }

    this.chatChannels = Array.from(channelMap.entries())
      .map(([channel, data]) => ({
        channel,
        bots: Array.from(data.bots),
        messageCount: data.count,
        lastActivity: this.extractTimestamp(data.lastKey)
      }))
      .sort((a, b) => b.lastActivity.localeCompare(a.lastActivity));
  }

  public openChannel(channel: string) {
    void this.router.navigate(['/bot-admin/project', this.projectId, 'groupchat', channel]);
  }

  private extractBotName(value: string): string {
    const colonIndex = value.indexOf(': ');
    if (colonIndex > -1) {
      return value.substring(0, colonIndex);
    }
    return 'Unbekannt';
  }

  private extractTimestamp(key: string): string {
    const prefix = key.startsWith('msg_') ? 'msg_' : 'msg';
    return key.substring(prefix.length);
  }
}
